import React, { useCallback, useEffect, useState } from 'react';
import { ActionIcon, Badge, Button, Group, Stack, Switch, Text, TextInput } from '@mantine/core';
import { useAppContext } from '../../contexts/AppContext';
import { PaneelKop, Sectie } from './bouwstenen';

// MapLibre levert feature-properties als strings: het gemeenten-veld komt
// binnen als JSON-tekst ('["Gouda","Woerden"]'). Eerst als JSON proberen,
// anders opschonen zodat er nooit quotes of haken in beeld staan.
export function parseGemeenten(waarde) {
  if (Array.isArray(waarde)) return waarde;
  if (!waarde) return [];
  const tekst = waarde.toString();
  try {
    const parsed = JSON.parse(tekst);
    if (Array.isArray(parsed)) return parsed.map(g => String(g).trim());
  } catch (e) { /* geen JSON: hieronder opschonen */ }
  return tekst
    .replace(/[[\]"]/g, '')
    .split(',')
    .map(g => g.trim())
    .filter(Boolean);
}

export default function RegioSidebar() {
  const {
    selectedRegio, setSelectedRegio, fetchWithAuth, addToast, user,
    regioJaarActief, setRegioJaarActief,
  } = useAppContext();
  const [aannemers, setAannemers] = useState([]);
  const [nieuw, setNieuw] = useState({ naam: '', toelichting: '' });
  const [bewerk, setBewerk] = useState(null);  // {id, naam, toelichting} tijdens bewerken
  const [bezig, setBezig] = useState(false);

  // Aannemerscombinaties beheren mag alleen met de admin-vlag
  const isAdmin = !!user?.admin;
  const regio = selectedRegio?.regio;

  const laadAannemers = useCallback(async () => {
    if (!regio) return;
    try {
      const res = await fetchWithAuth(`/api/regios/${encodeURIComponent(regio)}/aannemers`);
      const data = await res.json();
      if (data.success) setAannemers(data.aannemers);
    } catch (e) {
      console.warn('Aannemers laden mislukt:', e);
    }
  }, [regio, fetchWithAuth]);

  useEffect(() => { laadAannemers(); }, [laadAannemers]);

  if (!selectedRegio) return null;

  const gemeenten = parseGemeenten(selectedRegio.gemeenten);

  const voegToe = async () => {
    setBezig(true);
    try {
      const res = await fetchWithAuth(`/api/regios/${encodeURIComponent(regio)}/aannemers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(nieuw),
      });
      const data = await res.json();
      if (data.success) {
        setNieuw({ naam: '', toelichting: '' });
        laadAannemers();
      } else {
        addToast(data.error || 'Toevoegen mislukt', 'error');
      }
    } catch (e) {
      addToast('Toevoegen mislukt', 'error');
    } finally {
      setBezig(false);
    }
  };

  const wijzig = async () => {
    setBezig(true);
    try {
      const res = await fetchWithAuth(
        `/api/regios/${encodeURIComponent(regio)}/aannemers/${bewerk.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ naam: bewerk.naam, toelichting: bewerk.toelichting }),
        },
      );
      const data = await res.json();
      if (data.success) { setBewerk(null); laadAannemers(); }
      else addToast(data.error || 'Wijzigen mislukt', 'error');
    } catch (e) {
      addToast('Wijzigen mislukt', 'error');
    } finally {
      setBezig(false);
    }
  };

  const verwijder = async (id) => {
    try {
      const res = await fetchWithAuth(
        `/api/regios/${encodeURIComponent(regio)}/aannemers/${id}`,
        { method: 'DELETE' },
      );
      const data = await res.json();
      if (data.success) laadAannemers();
      else addToast(data.error || 'Verwijderen mislukt', 'error');
    } catch (e) {
      addToast('Verwijderen mislukt', 'error');
    }
  };

  return (
    <>
      <PaneelKop
        label="Regio"
        titel={selectedRegio.regio}
        subtitel={`${selectedRegio.aantal_gemeenten} gemeenten`}
        onSluit={() => setSelectedRegio(null)}
        offsetTop={44}
      />
      <Stack gap="md" p="md">
        <Sectie titel="Uitvoeringsjaren">
          <Switch
            checked={regioJaarActief}
            onChange={e => setRegioJaarActief(e.currentTarget.checked)}
            label="Buurten kleuren op uitvoeringsjaar"
          />
          <Text size="xs" c="dimmed">
            Toont voor de hele regio welke buurten in welk jaar aan de beurt zijn.
            De legenda verschijnt rechtsonder; klik daar een jaar om te filteren.
          </Text>
        </Sectie>

        <Sectie titel="Aannemerscombinaties">
          {aannemers.length === 0 && (
            <Text size="xs" c="dimmed">Nog geen aannemerscombinaties vastgelegd voor deze regio.</Text>
          )}
          {aannemers.map(a => (
            <div key={a.id}
              style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', paddingBottom: 4 }}>
              {bewerk && bewerk.id === a.id ? (
                <Group gap={6} wrap="nowrap">
                  <TextInput size="xs" placeholder="Combinatie" value={bewerk.naam} style={{ flex: 1 }}
                    onChange={e => { const w = e.currentTarget.value; setBewerk(b => ({ ...b, naam: w })); }} />
                  <TextInput size="xs" placeholder="Toelichting" value={bewerk.toelichting} style={{ flex: 1 }}
                    onChange={e => { const w = e.currentTarget.value; setBewerk(b => ({ ...b, toelichting: w })); }} />
                  <Button size="compact-xs" onClick={wijzig} loading={bezig} disabled={!bewerk.naam.trim()}>
                    Opslaan
                  </Button>
                  <Button size="compact-xs" variant="default" onClick={() => setBewerk(null)}>
                    Annuleer
                  </Button>
                </Group>
              ) : (
                <Group justify="space-between" wrap="nowrap">
                  <Stack gap={0} style={{ minWidth: 0 }}>
                    <Text size="sm" fw={500}>{a.naam}</Text>
                    {a.toelichting && <Text size="xs" c="dimmed">{a.toelichting}</Text>}
                  </Stack>
                  {isAdmin && (
                    <Group gap={2} wrap="nowrap">
                      <ActionIcon variant="subtle" size="sm" aria-label={`Bewerk ${a.naam}`}
                        onClick={() => setBewerk({ id: a.id, naam: a.naam, toelichting: a.toelichting || '' })}>
                        ✎
                      </ActionIcon>
                      <ActionIcon variant="subtle" color="red" size="sm"
                        aria-label={`Verwijder ${a.naam}`} onClick={() => verwijder(a.id)}>
                        ✕
                      </ActionIcon>
                    </Group>
                  )}
                </Group>
              )}
            </div>
          ))}
          {isAdmin && (
            <Group gap={6} wrap="nowrap">
              <TextInput size="xs" placeholder="Combinatie" value={nieuw.naam}
                style={{ flex: 1 }}
                onChange={e => { const w = e.currentTarget.value; setNieuw(n => ({ ...n, naam: w })); }} />
              <TextInput size="xs" placeholder="Toelichting (optioneel)" value={nieuw.toelichting}
                style={{ flex: 1 }}
                onChange={e => { const w = e.currentTarget.value; setNieuw(n => ({ ...n, toelichting: w })); }} />
              <Button size="compact-xs" onClick={voegToe} loading={bezig}
                disabled={!nieuw.naam.trim()}>
                Toevoegen
              </Button>
            </Group>
          )}
        </Sectie>

        {gemeenten.length > 0 && (
          <Sectie titel="Gemeenten in deze regio">
            <Group gap={6}>
              {gemeenten.sort().map((g, i) => (
                <Badge key={i} variant="light" color="groen" tt="none" size="lg" fw={500}>
                  {g}
                </Badge>
              ))}
            </Group>
          </Sectie>
        )}
      </Stack>
    </>
  );
}
